/** WizButler access model (apps >> modules >> grants) */

// short codes for the wiz apps (st = Setup, split out for better UX)
export type AppCode = "ms" | "st" | "pd" | "of" | "mk" | "rp" | "vc" | "ac";

// App/module registry. A module's INDEX in `modules` is its stored-grant bit
// therefore APPEND ONLY !!! , never reorder or reuse
export const APPS: AppDef[] = [
  {
    code: "ms",
    name: "Maestro (Operations)",
    modules: [
      { key: "bookings", name: "Bookings" },
      { key: "till", name: "Till" },
      { key: "panorama", name: "Panorama" },
      { key: "seating", name: "Seating" },
      { key: "customers", name: "Customers" },
      { key: "analytics", name: "Analytics" },
    ],
  },
  {
    code: "pd",
    name: "OMS",
    modules: [
      { key: "oms", name: "OMS" },
      { key: "menuControl", name: "Menu Control" },
    ],
  },
  {
    code: "of",
    name: "Office",
    modules: [
      { key: "products", name: "Products" },
      { key: "accounts", name: "Accounts" },
      { key: "cashUp", name: "Cash Up" },
      { key: "setup", name: "Setup", systemOnly: true },
    ],
  },
  {
    code: "ac",
    name: "Accounting",
    modules: [
      { key: "accounts", name: "Accounts" },
      { key: "cashUp", name: "Cash Up" },
      { key: "setup", name: "Setup", systemOnly: true },
    ],
  },
  {
    code: "mk",
    name: "Marketing",
    modules: [
      { key: "menus", name: "Menus" },
      { key: "membership", name: "Membership" },
      { key: "engagement", name: "Engagement" },
      { key: "giftCertificates", name: "Gift Certificates" },
      { key: "vouchers", name: "Vouchers" },
      { key: "reputation", name: "Reputation" }, // bit 5; existing full-app grants (mask 31) do NOT include it — grant explicitly
    ],
  },
  {
    code: "rp",
    name: "Reports & Insights",
    modules: [
      { key: "dailyAnalytics", name: "Daily Analytics" },
      { key: "revenueReports", name: "Revenue Reports" },
      { key: "patternRevenueReports", name: "Pattern Revenue Reports" },
      { key: "coverReports", name: "Cover Reports" },
      { key: "customerReports", name: "Customer Reports" },
      { key: "productsReports", name: "Products Reports" },
      { key: "functionReports", name: "Function Reports" },
      { key: "operatorReports", name: "Operator Reports" },
      { key: "customReports", name: "Custom Reports" },
    ],
  },
  {
    code: "vc",
    name: "Voice",
    modules: [{ key: "voice", name: "Voice" }],
  },
  {
    code: "st",
    name: "Setup",
    modules: [
      { key: "system", name: "System", systemOnly: true },
      { key: "userSetup", name: "User Setup" },
      { key: "generalSetup", name: "General Setup" },
      { key: "floorPlanSetup", name: "Floor Plan Setup" },
      { key: "serviceSetup", name: "Service Setup" },
      { key: "orderingSetup", name: "Ordering Setup" },
      { key: "functionSetup", name: "Function Setup" },
      { key: "postLabels", name: "Post Labels" },
      { key: "billingReports", name: "Billing Reports" },
    ],
  },
] as const;

// linked-module twins ("app:key") — granting/removing one mirrors the other (analytics ⇄ daily analytics)
export const LINKED_MODULES: ReadonlyArray<readonly [string, string]> = [["ms:analytics", "rp:dailyAnalytics"]] as const;

// one-way [dependent, prerequisite] deps — e.g. pd:menuControl can't exist without pd:oms
export const MODULE_DEPENDENCIES: ReadonlyArray<readonly [string, string]> = [["pd:menuControl", "pd:oms"]] as const;

// role tier > its bit value
export const SEED_ROLE_VALUE: Record<SeedRoleKey, number> = { station: 2, supervisor: 4, restaurantManager: 8, admin: 16 } as const;

// ordered seed-role keys (Station > Admin)
export const ROLE_KEYS: SeedRoleKey[] = ["station", "supervisor", "restaurantManager", "admin"] as const;

export interface NavMenuItem {
  name: string;
  path?: string;
  minRole?: number; // hard gate — ADMIN/SYSTEM only
  module?: string; // "app:module" grant gate (e.g. "mk:vouchers"); unset > minRole/legacy
  key?: string;
  children?: NavMenuItem[];
  icon?: any; // top-level items only; prefer LucideIcon
}

export interface NavMenuSection {
  section: string;
  items: NavMenuItem[];
  minRole?: number; // gate the whole section (unset > everyone)
}

export interface ModuleDef {
  key: string; // stable slug; its position is its stored-grant bit
  name: string;
  adminOnly?: boolean; // ADMIN/SYSTEM only; disabled for lower roles in the matrix
  systemOnly?: boolean; // never grantable to a tenant role
}
export interface AppDef {
  code: AppCode;
  name: string;
  modules: ModuleDef[]; // order defines each module's bit (index 0 = value 1); append-only
}

// { [appCode]: bitmask } — stored on WizUser.restaurants + in the derived claim
export interface AppGrants {
  [appCode: string]: number;
}

// a restaurant's role + its app grants
export interface RestaurantGrant {
  role: number;
  grants: AppGrants;
}

export interface RestaurantGrants {
  [restaurantId: string]: RestaurantGrant;
}

// per-restaurant grants from the FS access-staff doc; the switcher reads it (the claim carries only the active one)
export interface StaffRestaurantGrants {
  [restaurantId: string]: { grants?: AppGrants };
}

// viewer context for nav gating — the active restaurant's role + grants from the claim
export interface NavAccessContext {
  isSystem?: boolean; // SYSTEM sees everything
  roles?: number; // active restaurant's role (claim.roles) — minRole gate + legacy fallback
  grants?: AppGrants; // active restaurant's grants (claim.grants)
}

// the four seeded role tiers (Station > Admin)
export type SeedRoleKey = "station" | "supervisor" | "restaurantManager" | "admin";

// per-restaurant role definitions — FS /{clientId}/access/roles/{restaurantId}
export interface RoleDefinitionsDoc {
  station?: AppGrants;
  supervisor?: AppGrants;
  restaurantManager?: AppGrants;
  admin?: AppGrants;
  seededModules?: string[];
  seededAt?: number;
  updatedAt?: number;
}
